import RemoveIcon from '@mui/icons-material/Remove';
import { Button, CardContent, Grid, Stack, Tooltip, Typography } from '@mui/material';
import ProtectedLink from 'components/ProtectedLink';
import './OrderCard.scss';
import useManagementRights from 'hooks/rights/useManagementRights';
import { QuotesLocationState } from 'features/Quotes/Quotes';
import { trim } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import { Loader } from '../../../components';
import { useHistory } from 'react-router-dom';
import useManagementService from '../../Management/services/useManagementService';
import OrderMenu from '../OrderMenu/OrderMenu';
import { OrderCardProps } from './OrderCard';
import useOrderCardService from './useOrderCardService';
import { useAppState } from 'store/Provider';
import { injectOrderAction } from 'store/actions/catalogToOrderActions';

const OrderPendingCardContent: React.FC<OrderCardProps> = ({ order, action = true, menu = true, isFromMain }) => {
  const [t] = useTranslation();
  const { app, loadings } = useSelector((state) => state);
  const { dispatch } = useAppState();
  const history = useHistory();
  const { canEditOrder } = useManagementRights(order);
  const { orderClient, engagementDate, finalAmountPrice } = useOrderCardService(order);
  const { validateOrder } = useManagementService();

  const goToAffectationStep = () => {
    history.push(`/${app.customer?.slug}/orders/new-order/${order.id}`, {
      state: { initialStep: 3, order } as QuotesLocationState,
    });
    dispatch(injectOrderAction(order));
  };

  const handleProceed = async () => {
    if (isFromMain) {
      const orderUpdated = await validateOrder(order);
      if (orderUpdated) {
        goToAffectationStep();
      }
      return;
    }
    goToAffectationStep();
  };

  return (
    <CardContent>
      <Grid container spacing={4} justifyContent="space-between">
        <Grid item xs={12} md>
          <Grid
            container
            spacing={4}
            sx={{ '& .MuiGrid-item': { width: 0 }, justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} md={6} lg={2}>

                {order.affair ? (
                  <Tooltip title={trim([order.affair?.otp, order.affair?.name].join(' - '))}>
                    <div>
                      <Typography noWrap className="order-card-title">
                        {order.affair?.otp}
                      </Typography>
                      <Typography noWrap className="order-card-title">
                        {order.affair?.name}
                      </Typography>
                    </div>
                  </Tooltip>
                ) : (
                <Typography noWrap>{t('Affair_Not_Indicated')} </Typography>
                )}

            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography noWrap className="order-card-label">
                {t('Client')}
              </Typography>
              <Tooltip title={orderClient()}>
                <Typography noWrap className="order-card-value">
                  {orderClient()}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography className="order-card-label">{t('Project_Name')}</Typography>
              <Tooltip title={order?.name_spec}>
                <Typography noWrap className="order-card-value">
                  {order?.name_spec || 'Not defined'}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} lg={2} sx={{ display: 'none' }}>
              <Typography className="order-card-label">{t('Chrono reference')}</Typography>
              <Tooltip title={order.draft_reference ?? t<string>('Not_Defined')}>
                {order.draft_reference ? (
                  <Typography noWrap className="order-card-value">
                    {order.draft_reference}
                  </Typography>
                ) : (
                  <RemoveIcon color="disabled" />
                )}
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography className="order-card-label">{t('Engagement_Date')}</Typography>
              <Tooltip title={engagementDate()}>
                <Typography noWrap className="order-card-value">
                  {engagementDate()}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography className="order-card-label">{t('Price')}</Typography>
              <Tooltip title={finalAmountPrice()}>
                <Typography noWrap className="order-card-value">
                  {finalAmountPrice()}
                </Typography>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          md="auto"
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', ml: 'auto' }}
        >
          <Stack spacing={2} direction="row" alignItems="center">
            {action && canEditOrder && (
              <Tooltip title={order.final_amount_price === 0 ? t('Create_Work_Unit_To_Proceed') : ''}>
                <span>
                  <Button variant="contained" onClick={handleProceed} disabled={order.final_amount_price === 0}>
                    {loadings.completeOrderLoading ? <Loader size={20} className="white-loader" /> : t('Go_Ahead')}
              </Button>
                </span>
              </Tooltip>
            )}
            {menu && <OrderMenu order={order} />}
          </Stack>
        </Grid>
      </Grid>
    </CardContent>
  );
};

export default OrderPendingCardContent;
